import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query"
import {
  getChats,
  getThreadChats,
  postThreadChats,
  uploadMediaFile,
  sendTemplate,
  updateThreadRead,
} from "../api/chats"

export const useFetchChats = (limit, search, isRequested, isUnread) => {
  return useInfiniteQuery({
    queryKey: ["chats", search, isRequested, isUnread],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await getChats({
        offset: pageParam * limit,
        limit,
        search,
        isRequested,
        isUnread,
      })
      return res.data
    },
    getNextPageParam: (lastPage, pages) => {
      const totalFetched = pages.length * limit
      if (totalFetched >= lastPage.data.total) {
        return undefined
      }
      return pages.length
    },
    select: (data) => ({
      threads: data.pages
        .flatMap((page) =>
          (page?.data?.threads || []).map((thread) => ({
            ...thread,
            phone: thread.phone_number,
            name: thread.user_name,
          }))
        )
        .filter(Boolean), // Filter out any undefined entries
      total: data.pages[0]?.data?.total || 0,
    }),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    cacheTime: 5 * 60 * 1000,
    staleTime: 1 * 60 * 1000,
    retry: 2, // Add retry attempts
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

export const useFetchThreadChats = (threadId, offset, limit, search) => {
  return useInfiniteQuery({
    queryKey: ["thread-chats", threadId],
    queryFn: async ({ pageParam = 0 }) => {
      console.log("Fetching page:", pageParam)
      const res = await getThreadChats({
        threadId,
        offset: pageParam * limit,
        limit,
        search,
      })
      return res.data
    },
    getNextPageParam: (lastPage, pages) => {
      // Add debug logging
      console.log("Last page messages:", lastPage?.data?.messages?.length)
      console.log("Total pages:", pages.length)

      if (!lastPage?.data?.messages || lastPage.data.messages.length === 0) {
        return undefined
      }
      // If we received fewer messages than limit, we've reached the end
      if (lastPage.data.messages.length < limit) {
        return undefined
      }
      // Return next page number
      return pages.length
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: !!threadId,
    staleTime: Infinity,
    cacheTime: Infinity,
    select: (data) => ({
      messages: data.pages.flatMap((page) =>
        page.data.messages.map((message) => ({
          ...message,
          message: message.content,
        }))
      ),
    }),
  })
}

export const usePostThreadChat = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ threadId, message, files = [] }) => {
      try {
        const res = await postThreadChats({ threadId, message, files })
        return res.data
      } catch (error) {
        throw error
      }
    },
    // onSuccess: (data, variables) => {
    //   queryClient.invalidateQueries({
    //     queryKey: ["thread-chats", variables.threadId],
    //     refetchType: "all",
    //   })
    // },
  })
}

export const useUploadMediaFile = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ threadId, mediaFile }) => {
      try {
        const res = await uploadMediaFile({ threadId, mediaFile })
        return res.data
      } catch (error) {
        throw error
      }
    },
    // onSuccess: (data, variables) => {
    //   queryClient.invalidateQueries({
    //     queryKey: ["thread-chats", variables.threadId],
    //     refetchType: "all",
    //   })
    // },
  })
}

export const useSendTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (threadId) => {
      const res = await sendTemplate(threadId)
      return res.data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["thread-chats", variables],
        refetchType: "all",
      })
    },
  })
}

export const useUpdateThreadRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables) => {
      const { threadId, search, isUnread, isRequested } = variables
      const res = await updateThreadRead(threadId)
      return res.data
    },
    onSuccess: (_, variables) => {
      const { threadId, search, isUnread, isRequested } = variables
      console.log("#SDT Thread ID:", threadId, search, isUnread, isRequested)
      // Update the threads list to mark this thread as read
      const data = queryClient.getQueryData([
        "chats",
        search,
        isUnread,
        isRequested,
      ])
      console.log("#SDT Data before update:", data)
      const threads = data?.pages?.[0]?.data?.threads || []
      if (!threads.length) return

      // lets find the thread in the threads list
      const threadIndex = threads.findIndex((th) => th.thread_id === threadId)
      if (threadIndex === -1) return
      // update the thread is_read and unread_count
      const thread = threads[threadIndex]
      thread.is_read = true
      thread.unread_count = 0
      // now save the updated thread back to the cache
      console.log("#SDT Thread before update:", thread)
      threads[threadIndex] = thread
      // update the thread in pages[0].data.threads
      const updatedData = {
        ...data,
        pages: [
          {
            ...data.pages[0],
            data: {
              ...data.pages[0].data,
              threads,
            },
          },
        ],
      }
      console.log("#SDT Updated data:", updatedData)
      queryClient.setQueryData(["chats", "", false, false], updatedData)

      console.log("#SDT Updated data:", updatedData)
    },
  })
}
